import {
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Paper,
  Select,
  Switch,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Segment } from '../../types/segment.type';
import { SegmentCategory } from '../../types/segment-category.type';
import { SEGMENT_CATEGORIES, SEGMENT_CATEGORY_LABELS } from '../../constants/segment-category.constants';
import { SEGMENT_RATE_ERROR, SEGMENT_RATE_MAX } from '../../utils/segment-rate.util';
import { SEGMENT_NAME_ERROR } from '../../utils/segment-name.util';

type SegmentFieldChange = { field: keyof Segment; value: string | number | boolean };

const useStyles = makeStyles((theme) => ({
  paperContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  switchBase: {
    '&$switchChecked': {
      color: theme.palette.admin.primary.main,
    },
    '&$switchChecked + $switchTrack': {
      backgroundColor: theme.palette.admin.primary.main,
    },
  },
  switchChecked: {},
  switchTrack: {},
}));

interface Props {
  updatedSegment: Segment;
  isArchived: boolean;
  isNameInvalid: boolean;
  isRateInvalid: boolean;
  onFieldChange: (change: SegmentFieldChange) => void;
}

export function SegmentInformationsCard({
  updatedSegment,
  isArchived,
  isNameInvalid,
  isRateInvalid,
  onFieldChange,
}: Props) {
  const classes = useStyles();

  return (
    <section>
      <h4>Informations</h4>
      <Paper className={classes.paperContent}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              color="default"
              classes={{ switchBase: classes.switchBase, checked: classes.switchChecked, track: classes.switchTrack }}
              disabled={isArchived}
              checked={updatedSegment.isActive}
              onChange={(event) => onFieldChange({ field: 'isActive', value: event.target.checked })}
            />
          }
          label="Actif"
        />
        <TextField
          id="segment-name"
          label="Nom"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          error={isNameInvalid}
          helperText={isNameInvalid ? SEGMENT_NAME_ERROR : undefined}
          value={updatedSegment.name}
          onChange={(event) => onFieldChange({ field: 'name', value: event.target.value })}
        />
        <div className={classes.row}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel htmlFor="segment-category">Catégorie</InputLabel>
            <Select
              native
              label="Catégorie"
              value={updatedSegment.category}
              inputProps={{ id: 'segment-category' }}
              onChange={(event) => onFieldChange({ field: 'category', value: event.target.value as SegmentCategory })}
            >
              {SEGMENT_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {SEGMENT_CATEGORY_LABELS[category]}
                </option>
              ))}
            </Select>
          </FormControl>
          <TextField
            id="segment-rate"
            label="Taux commercial"
            type="number"
            size="small"
            variant="outlined"
            fullWidth
            value={updatedSegment.amount}
            inputProps={{ min: 0, max: SEGMENT_RATE_MAX, step: 0.01 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            error={isRateInvalid}
            helperText={isRateInvalid ? SEGMENT_RATE_ERROR : undefined}
            onChange={(event) => onFieldChange({ field: 'amount', value: Number(event.target.value) })}
          />
        </div>
        <TextField
          id="segment-description"
          label="Description"
          type="text"
          size="small"
          variant="outlined"
          multiline
          minRows={3}
          maxRows={3}
          fullWidth
          value={updatedSegment.description ?? ''}
          onChange={(event) => onFieldChange({ field: 'description', value: event.target.value })}
        />
      </Paper>
    </section>
  );
}
